import { ReactNode } from 'react';
import { FiAlertCircle } from 'react-icons/fi';
import caixa from '../../assets/redesign/images/caixa.png';
import problemas from '../../assets/redesign/images/problemas.png';
import {
  ActionButtons,
  BannerBg,
  Block,
  BlockTitle,
  Columns,
  CtaSection,
  FinancingActions,
  FinancingBtn,
  FinancingCard,
  FinancingSubtitle,
  FinancingTitleRow,
  GallerySection,
  LeftCol,
  LocationLabel,
  MapSlot,
  PagePad,
  ProblemsCard,
  ProblemsContent,
  ProblemsImage,
  ProblemsLinks,
  ProblemsTitleRow,
  RelatedSection,
  RightCol,
  ScheduleDesktop,
  ScheduleMobile,
  Section,
  ShareToolbar,
  Title,
  TitleBlock,
  TitleRow,
} from './styles';

export type PropertyDetailVariant = 'imovel' | 'empreendimento';

export interface PropertyDetailLayoutProps {
  variant: PropertyDetailVariant;
  titleIdDisplay?: string;
  titleText: string;
  locationLabel?: string;
  actionButtons?: ReactNode;
  gallery?: ReactNode;
  shareToolbar?: ReactNode;
  description?: ReactNode;
  overview?: ReactNode;
  saibaMais?: ReactNode;
  locationInfo?: ReactNode;
  mapSlot?: ReactNode;
  priceBlock?: ReactNode;
  scheduleDesktop?: ReactNode;
  scheduleMobile?: ReactNode;
  relatedSection?: ReactNode | null;
  onOuvidoria?: (motivo?: string) => void;
  children?: ReactNode;
  idDisplayForOuvidoria?: string;
}

const CAIXA_URL =
  'https://www.caixa.gov.br/voce/habitacao/financiamento-de-imoveis/Paginas/default.aspx';

export function PropertyDetailLayout({
  variant,
  titleIdDisplay,
  titleText,
  locationLabel,
  actionButtons,
  gallery,
  shareToolbar,
  description,
  overview,
  saibaMais,
  locationInfo,
  mapSlot,
  priceBlock,
  scheduleDesktop,
  scheduleMobile,
  relatedSection,
  onOuvidoria,
  children,
  idDisplayForOuvidoria,
}: PropertyDetailLayoutProps) {
  const idLabel = idDisplayForOuvidoria || titleIdDisplay || '';
  const saibaMaisTitle =
    variant === 'empreendimento'
      ? 'Saiba mais sobre este empreendimento'
      : 'Saiba mais sobre este imóvel';

  const handleOuvidoria = (kind: 'vendido' | 'resposta' | 'outros') => {
    if (!onOuvidoria) return;
    if (kind === 'vendido') {
      onOuvidoria(`O imóvel ${idLabel} está vendido ou reservado`);
      return;
    }
    if (kind === 'resposta') {
      onOuvidoria(`Não consigo obter resposta sobre o imóvel ${idLabel}`);
      return;
    }
    onOuvidoria();
  };

  return (
    <Section>
      <BannerBg aria-hidden />

      <PagePad>
        <TitleBlock>
          <Title>
            {titleIdDisplay ? <b>{titleIdDisplay}</b> : null}
            {titleIdDisplay ? ' - ' : ''}
            {titleText}
          </Title>
          <TitleRow>
            {locationLabel ? <LocationLabel>{locationLabel}</LocationLabel> : <span />}
            {actionButtons ? <ActionButtons>{actionButtons}</ActionButtons> : null}
          </TitleRow>
        </TitleBlock>
      </PagePad>

      {(gallery || shareToolbar) && (
        <PagePad>
          <GallerySection>
            {gallery}
            {shareToolbar ? <ShareToolbar>{shareToolbar}</ShareToolbar> : null}
          </GallerySection>
        </PagePad>
      )}

      <PagePad>
        <Columns>
          <LeftCol>
            {description !== undefined && description !== null && (
              <Block>
                <div>
                  <BlockTitle>Descrição</BlockTitle>
                </div>
                {description}
              </Block>
            )}

            {overview !== undefined && overview !== null && (
              <Block>
                <BlockTitle>Visão Geral</BlockTitle>
                {overview}
              </Block>
            )}

            {saibaMais !== undefined && saibaMais !== null && (
              <Block>
                <BlockTitle>{saibaMaisTitle}</BlockTitle>
                {saibaMais}
              </Block>
            )}

            {(locationInfo || mapSlot) && (
              <Block>
                <BlockTitle>Localização</BlockTitle>
                {locationInfo}
                {mapSlot ? <MapSlot>{mapSlot}</MapSlot> : null}
              </Block>
            )}
          </LeftCol>

          <RightCol>
            {priceBlock}
            {scheduleDesktop ? (
              <ScheduleDesktop>{scheduleDesktop}</ScheduleDesktop>
            ) : null}
          </RightCol>
        </Columns>
      </PagePad>

      {relatedSection ? (
        <PagePad>
          <RelatedSection>{relatedSection}</RelatedSection>
        </PagePad>
      ) : null}

      {scheduleMobile ? (
        <PagePad>
          <ScheduleMobile>
            <div>{scheduleMobile}</div>
          </ScheduleMobile>
        </PagePad>
      ) : null}

      <PagePad>
        <CtaSection>
          <FinancingCard>
            <div>
              <FinancingTitleRow>
                <h4>Financie seu imóvel com a</h4>
                <img src={caixa} alt="Caixa" />
              </FinancingTitleRow>
              <FinancingSubtitle>Parcelas que cabem no seu bolso</FinancingSubtitle>
            </div>
            <FinancingActions>
              <FinancingBtn
                className="primary"
                href={CAIXA_URL}
                target="_blank"
                rel="noreferrer"
              >
                Solicitar financiamento
              </FinancingBtn>
              <FinancingBtn
                className="secondary"
                href={CAIXA_URL}
                target="_blank"
                rel="noreferrer"
              >
                Saiba mais
              </FinancingBtn>
            </FinancingActions>
          </FinancingCard>

          <ProblemsCard>
            <ProblemsContent>
              <ProblemsTitleRow>
                <FiAlertCircle />
                <h4>Enfrentando algum problema com este anúncio?</h4>
              </ProblemsTitleRow>
              <ProblemsLinks>
                <button type="button" onClick={() => handleOuvidoria('vendido')}>
                  O imóvel está vendido ou reservado
                </button>
                <button type="button" onClick={() => handleOuvidoria('resposta')}>
                  Não consigo obter resposta
                </button>
                <button type="button" onClick={() => handleOuvidoria('outros')}>
                  Outros motivos
                </button>
              </ProblemsLinks>
            </ProblemsContent>
            <ProblemsImage src={problemas} alt="Problemas com o anúncio" />
          </ProblemsCard>
        </CtaSection>
      </PagePad>

      {children}
    </Section>
  );
}

export * from './styles';
